import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import { BASE_URL } from "../utils/reportsHelpers";
import text from "../utils/reportsTranslations";

const useReportsData = (language) => {
  const { profile } = useProfile();
  const [reportsData, setReportsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState({ type: "", message: "" });
  const [quickStats, setQuickStats] = useState({
    totalReports: 24,
    generatedThisMonth: 8,
    totalDownloads: 1247,
    avgGenerationTime: "3.2 min",
    activeUsers: 45,
    diskUsage: "78%",
    successRate: "98.5%",
    avgRating: 4.6
  });

  // Derive infinityCode from profile (with localStorage fallback)
  const currentUser = profile?.infinityCode
    ? String(profile.infinityCode)
    : localStorage.getItem('infinityCode') || null;

  useEffect(() => {
    const fetchReports = async () => {
      if (!currentUser) {
        console.log('⏳ Waiting for user info...');
        return;
      }

      try {
        console.log('📊 Fetching reports for infinityCode:', currentUser);

        const res = await apiFetch(
          `${BASE_URL}/reports/metadata?infinityCode=${currentUser}`,
          { method: 'GET' }
        );


        if (!res.ok) {
          const errorData = await res.json();
          throw new Error(errorData.error || `HTTP error! status: ${res.status}`);
        }

        const data = await res.json();
        console.log('✅ Reports loaded:', data.reports?.length);

        setReportsData(data.reports || []);

        if (data.statistics) {
          setQuickStats({
            totalReports: data.reports?.length || 0,
            generatedThisMonth: data.statistics.attendanceThisMonth || 0,
            totalDownloads: 1247,
            avgGenerationTime: "3.2 " + text[language].min,
            totalEmployees: data.statistics.totalEmployees || 0,
            activeEmployees: data.statistics.activeEmployees || 0,
            avgAttendanceRate: data.statistics.attendanceRate || '0%',
            recentHires: data.statistics.recentHires || 0
          });
        }
      } catch (err) {
        console.error("❌ Error fetching reports:", err);
        setEmailStatus({
          type: "error",
          message: err.message || text[language].errorGeneratingReport
        });
      }
    };

    fetchReports();
  }, [currentUser, language]);

  const generateNewReport = async () => {
    setIsLoading(true);
    try {
      const res = await apiFetch(`${BASE_URL}/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filters: {
            requestingUser: currentUser
          }
        })
      });

      const newReport = await res.json();

      setReportsData(prev => [...prev, newReport]);
      setEmailStatus({
        type: "success",
        message: text[language].reportGenerateSuccess
      });
    } catch (err) {
      setEmailStatus({
        type: "error",
        message: text[language].errorGeneratingReport
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadReport = async (report, selectedFormat) => {
    if (!currentUser) {
      setEmailStatus({
        type: "error",
        message: text[language].userInfoNotAvailable
      });
      return;
    }

    try {
      const format = selectedFormat || 'pdf';

      const response = await apiFetch(`${BASE_URL}/reports/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: report.category,
          format: format,
          filters: {
            requestingUser: currentUser
          }
        })
      });

      if (!response.ok) {
        throw new Error(text[language].errorDownloadingReport);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const extension = format === 'xlsx' ? 'xlsx' : 'pdf';
      a.download = `${report.category}-report-${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setEmailStatus({
        type: "success",
        message: text[language].reportDownloadSuccess
      });
    } catch (error) {
      setEmailStatus({
        type: "error",
        message: text[language].errorDownloadingReport
      });
    }
  };
const toggleBookmark = (reportId) => {
  setReportsData(prev =>
    prev.map(report =>
      report.id === reportId ? { ...report, isBookmarked: !report.isBookmarked } : report
    )
  );
};

return {
  reportsData,
  setReportsData,
  currentUser,
  isLoading,
  quickStats,
  emailStatus,
  setEmailStatus,
  generateNewReport,
  handleDownloadReport,
  toggleBookmark,
};
};

export default useReportsData;